import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Share2, X } from "lucide-react";
import { useRegisterSW } from "virtual:pwa-register/react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { ensurePushSubscription, requestPushPermission, supportsPushNotifications } from "@/lib/push-notifications";
import { BeforeInstallPromptEvent, isIosDevice, isStandaloneMode, PWA_DISMISSED_KEY } from "@/lib/pwa";

async function enableNativeNotifications() {
  if (!supportsPushNotifications()) {
    return;
  }

  const permission = await requestPushPermission();

  if (permission === "granted") {
    await ensurePushSubscription();
  }
}

export function InstallPWA() {
  const isMobile = useIsMobile();
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(PWA_DISMISSED_KEY) === "true");
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState(() => isStandaloneMode());
  const [showIosInstructions, setShowIosInstructions] = useState(false);
  const ios = useMemo(() => isIosDevice(), []);

  useRegisterSW({
    immediate: true,
  });

  const dismissBanner = useCallback(() => {
    localStorage.setItem(PWA_DISMISSED_KEY, "true");
    setDismissed(true);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(display-mode: standalone)");

    const syncStandaloneState = () => {
      setIsStandalone(isStandaloneMode());
    };

    syncStandaloneState();
    mediaQuery.addEventListener("change", syncStandaloneState);

    return () => {
      mediaQuery.removeEventListener("change", syncStandaloneState);
    };
  }, []);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    };

    const handleInstalled = async () => {
      setDeferredPrompt(null);
      setIsStandalone(true);
      dismissBanner();
      await enableNativeNotifications();
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, [dismissBanner]);

  useEffect(() => {
    if (!isStandalone) {
      return;
    }

    void enableNativeNotifications();
  }, [isStandalone]);

  const handleInstall = useCallback(async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;

      if (result.outcome === "accepted") {
        dismissBanner();
      }

      setDeferredPrompt(null);
      return;
    }

    if (ios) {
      setShowIosInstructions((current) => !current);
    }
  }, [deferredPrompt, dismissBanner, ios]);

  if (!isMobile || dismissed || isStandalone || (!deferredPrompt && !ios)) {
    return null;
  }

  return (
    <div className="fixed inset-x-0 bottom-4 z-50 px-4">
      <Card className="mx-auto max-w-md border-primary/20 bg-background/95 p-4 shadow-2xl backdrop-blur">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Download className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground">Instale o Arqum no seu celular</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Acesse demandas, conteúdo e notificações como um app nativo.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" className="gap-2" onClick={() => void handleInstall()}>
                {ios ? <Share2 className="h-4 w-4" /> : <Download className="h-4 w-4" />}
                Instalar
              </Button>
              <Button size="sm" variant="ghost" onClick={dismissBanner}>
                Agora não
              </Button>
            </div>
            <div
              className={cn(
                "mt-3 rounded-lg bg-muted/60 px-3 py-2 text-sm text-muted-foreground",
                !showIosInstructions && "hidden",
              )}
            >
              Toque em compartilhar → Adicionar à tela de início
            </div>
          </div>
          <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={dismissBanner}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </Card>
    </div>
  );
}
